import React from 'react';

/**
 * EmojiReactionBar Component
 * Displays emoji reactions with counts and allows toggling
 */
export default function EmojiReactionBar({
    reactions = {},
    currentUserId,
    onReactionToggle,
    getEmoteObject, // Function injected from parent
    checkAccess // Function injected from parent (optional)
}) {
    // Get list of [emojiId, userIds[]] pairs that have reactions
    const reactionEntries = Object.entries(reactions).filter(([_, users]) => users && users.length > 0);

    if (reactionEntries.length > 0) {
        // Debugging logs
        // console.log('EmojiReactionBar Rendering:', { reactionEntries, hasGetEmoteObject: !!getEmoteObject });
    }

    if (reactionEntries.length === 0) return null;

    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginTop: '10px'
        }}>
            {reactionEntries.map(([emojiId, userIds]) => {
                // Resolve Emote Data (Theme Aware)
                // If getEmoteObject is missing (legacy/error), fallback safely
                const emoteObj = getEmoteObject ? getEmoteObject(emojiId) : null;

                // Fallback for standard unicode or if data missing
                const displayName = emoteObj?.name || 'Unknown';
                const displayUrl = emoteObj?.displayUrl; // From hook logic
                let displayChar = emoteObj?.emoji || emojiId;

                // If it looks like a Firestore ID (alphanumeric > 10 chars) and no URL, show fallback
                if (!displayUrl && displayChar.length > 8 && !displayChar.match(/^:.*:$/)) {
                    displayChar = '❓';
                }

                const hasReacted = userIds.includes(currentUserId);
                const count = userIds.length;

                return (
                    <button
                        key={emojiId}
                        onClick={() => {
                            // Validate Access
                            if (!hasReacted && checkAccess) {
                                const { hasAccess, reason } = checkAccess(emojiId);
                                if (!hasAccess) {
                                    alert(reason || "You do not have access to this emote.");
                                    return;
                                }
                            }
                            onReactionToggle(emojiId);
                        }}
                        title={`${count} ${displayName} reaction${count !== 1 ? 's' : ''}`}
                        style={{
                            background: hasReacted ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                            border: hasReacted ? '1px solid rgba(139, 92, 246, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            padding: '4px 10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: hasReacted ? '0 0 8px rgba(139, 92, 246, 0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = hasReacted
                                ? 'rgba(139, 92, 246, 0.3)'
                                : 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = hasReacted
                                ? 'rgba(139, 92, 246, 0.2)'
                                : 'rgba(255,255,255,0.05)';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        {displayUrl ? (
                            <img
                                src={displayUrl}
                                alt={displayName}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : (
                            <span style={{ fontSize: '16px' }}>{displayChar}</span>
                        )}
                        <span style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: hasReacted ? '#8b5cf6' : '#999'
                        }}>
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
